import type {
  ProposalContentOperation,
  ProposalResolutionState,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  canonicalProposalCollectionItem,
  proposalSemanticJsonValuesEqual,
} from "work/proposals/negotiation/proposal-collection-codec";
import { getProposalCollectionItemIdentity } from "work/proposals/negotiation/proposal-operation-identity";

export type ProposalResolutionTargetIdentity = {
  path: string;
  conflictKey: string;
  itemId?: string | null;
};

export function proposalConflictKeyContains(parentConflictKey: string, candidateConflictKey: string): boolean {
  const parent = parentConflictKey.toLowerCase();
  const candidate = candidateConflictKey.toLowerCase();
  return candidate === parent || candidate.startsWith(`${parent}:`);
}

/** True when a content operation changes exactly a scalar target or any leaf of a stable item target. */
export function proposalContentOperationTargetsResolutionTarget(
  operation: ProposalContentOperation,
  target: ProposalResolutionTargetIdentity,
): boolean {
  if (operation.kind === "ResolutionDecision" || operation.path !== target.path) return false;
  const targetItemId = canonicalItemId(target.itemId);
  if (!targetItemId) return operation.conflictKey.toLowerCase() === target.conflictKey.toLowerCase();
  if (!("itemId" in operation) || canonicalItemId(operation.itemId) !== targetItemId) return false;
  return proposalConflictKeyContains(target.conflictKey, operation.conflictKey);
}

/** Exact captured-target match used for local projection and stale-decision pruning. */
export function proposalResolutionDecisionTargetsState(
  operation: Extract<ProposalContentOperation, { kind: "ResolutionDecision" }>,
  state: ProposalResolutionState,
): boolean {
  const operationHasTarget = Object.prototype.hasOwnProperty.call(operation, "targetValue");
  if (
    operation.conflictKey.toLowerCase() !== state.conflictKey.toLowerCase() ||
    operation.path !== state.path ||
    canonicalItemId(operation.itemId) !== canonicalItemId(state.itemId) ||
    operationHasTarget !== state.targetValueExists
  ) {
    return false;
  }
  if (!operationHasTarget) return true;

  const operationTarget = state.itemId
    ? canonicalProposalCollectionItem(state.path, operation.targetValue)
    : operation.targetValue;
  const stateTarget = state.itemId
    ? canonicalProposalCollectionItem(state.path, state.targetValue)
    : state.targetValue;
  return proposalSemanticJsonValuesEqual(operationTarget, stateTarget);
}

function canonicalItemId(itemId: string | null | undefined): string | null {
  return itemId ? (getProposalCollectionItemIdentity(itemId) ?? itemId.toLowerCase()) : null;
}
